import { memo, useMemo } from 'react';

interface SparklineCellProps {
  data: number[];
  type?: 'line' | 'bar' | 'area';
  color?: string;
  width?: number;
  height?: number;
}

export const SparklineCell = memo(({
  data,
  type = 'line',
  color = 'var(--color-primary)',
  width = 80,
  height = 24
}: SparklineCellProps) => {
  const { path, areaPath } = useMemo(() => {
    if (!data || data.length < 2) return { path: '', areaPath: '' };

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const padding = 2;

    const points = data.map((value, index) => ({
      x: padding + (index / (data.length - 1)) * (width - padding * 2),
      y: padding + (1 - (value - min) / range) * (height - padding * 2)
    }));

    const linePath = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');

    const areaPathStr = `${linePath} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`;

    return { path: linePath, areaPath: areaPathStr };
  }, [data, width, height]);

  if (!data || data.length < 2) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const lastValue = data[data.length - 1];
  const firstValue = data[0];
  const trend = lastValue >= firstValue ? 'up' : 'down';
  const trendColor = trend === 'up' ? 'var(--color-positive)' : 'var(--color-negative)';
  const lineColor = color === 'var(--color-primary)' ? trendColor : color;

  if (type === 'bar') {
    const max = Math.max(...data);
    const barWidth = (width - 2) / data.length - 1;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '1px',
          height: `${height}px`,
          width: `${width}px`
        }}
      >
        {data.map((value, index) => {
          const barHeight = Math.max(2, (value / max) * (height - 4));
          const isLast = index === data.length - 1;
          return (
            <div
              key={index}
              style={{
                width: `${barWidth}px`,
                height: `${barHeight}px`,
                background: isLast ? lineColor : `${lineColor}50`,
                borderRadius: '1px',
                transition: 'height 0.2s ease'
              }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
    >
      {type === 'area' && (
        <path
          d={areaPath}
          fill={`${lineColor}20`}
        />
      )}
      <path
        d={path}
        fill="none"
        stroke={lineColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End dot */}
      <circle
        cx={2 + ((data.length - 1) / (data.length - 1)) * (width - 4)}
        cy={2 + (1 - (lastValue - Math.min(...data)) / (Math.max(...data) - Math.min(...data) || 1)) * (height - 4)}
        r={2.5}
        fill={lineColor}
      />
    </svg>
  );
});

SparklineCell.displayName = 'SparklineCell';
